import * as THREE from 'three';

/**
 * Owns the root {@link THREE.Scene} and the content roots that domain systems attach to. The scene
 * graph is a *view* reconstructed from canonical data — it is never the source of truth.
 */
export class SceneManager {
  readonly scene: THREE.Scene;
  /** Root for terrain meshes. */
  readonly terrainRoot: THREE.Group;
  /** Root for flora (instanced batches + temporary editable representations). */
  readonly floraRoot: THREE.Group;
  /** Root for site objects — buildings and paving (`src/site/`). */
  readonly siteRoot: THREE.Group;
  /** Root for lights and environment helpers. */
  readonly environmentRoot: THREE.Group;
  /** Root for editor overlays (ghosts, selection outlines, gizmos). */
  readonly overlayRoot: THREE.Group;

  constructor() {
    this.scene = new THREE.Scene();
    // A flat stand-in only. `SkyEnvironment` replaces this with a gradient dome as soon as the
    // application wires one up; this is what a scene built without one (a test, a headless capture
    // harness) renders against, and it is the old sky's colour so those keep looking as they did.
    this.scene.background = new THREE.Color(0x87b6e8);

    this.terrainRoot = new THREE.Group();
    this.terrainRoot.name = 'terrain-root';
    this.floraRoot = new THREE.Group();
    this.floraRoot.name = 'flora-root';
    this.siteRoot = new THREE.Group();
    this.siteRoot.name = 'site-root';
    this.environmentRoot = new THREE.Group();
    this.environmentRoot.name = 'environment-root';
    this.overlayRoot = new THREE.Group();
    this.overlayRoot.name = 'overlay-root';

    this.scene.add(
      this.terrainRoot,
      this.floraRoot,
      this.siteRoot,
      this.environmentRoot,
      this.overlayRoot,
    );
  }

  /** Recursively dispose geometries/materials under the scene and clear children. */
  dispose(): void {
    this.scene.traverse((obj) => {
      const mesh = obj as Partial<THREE.Mesh>;
      mesh.geometry?.dispose();
      const material = mesh.material;
      if (Array.isArray(material)) {
        material.forEach((m) => m.dispose());
      } else {
        material?.dispose();
      }
    });
    this.scene.clear();
  }
}
